import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Loader2 } from "lucide-react";

const PRIORITY_LABELS = { essenziale: "Essenziale", utile: "Utile", opzionale: "Opzionale" };
const PRIORITY_STYLES = {
  essenziale: "bg-rose-100 text-rose-700 border-rose-300",
  utile: "bg-blue-100 text-blue-700 border-blue-300",
  opzionale: "bg-slate-100 text-slate-600 border-slate-300",
};
const PRIORITY_ORDER = { essenziale: 1, utile: 2, opzionale: 3 };

/**
 * Lista a checkbox di garanzie selezionabili manualmente dall'operatore in base alle scelte del cliente.
 * Carica le garanzie da /api/policy-coverages filtrandole per tipologia polizza.
 *
 * Props:
 *  - policyType: codice della tipologia polizza (es. "auto", "casa", "vita"). "" o null = tutte.
 *  - value: array di codici selezionati (es. ["rca", "kasko"])
 *  - onChange: (newValue: string[], newLabels: string[]) => void
 *  - publicMode: se true usa endpoint pubblico /public/policy-coverages (no auth)
 *  - testid: opzionale, prefisso data-testid
 *  - className: opzionale
 */
export const CoveragePicker = ({ policyType, value = [], onChange, publicMode = false, testid = "coverage-picker", className = "" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = policyType ? { type: policyType } : {};
      const endpoint = publicMode ? "/public/policy-coverages" : "/policy-coverages";
      let data;
      if (publicMode) {
        const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
        const res = await axios.get(apiBase + endpoint, { params });
        data = res.data;
      } else {
        const res = await api.get(endpoint, { params });
        data = res.data;
      }
      setItems(data || []);
    } catch (e) {
      setError("Impossibile caricare le garanzie");
    } finally {
      setLoading(false);
    }
  }, [policyType, publicMode]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => new Set(value || []), [value]);

  const toggle = (code, label) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    const codes = [...next];
    const labels = items.filter((c) => next.has(c.code)).map((c) => c.label);
    onChange?.(codes, labels);
  };

  const selectedLabels = useMemo(
    () => items.filter((c) => selected.has(c.code)).map((c) => c.label),
    [items, selected],
  );

  return (
    <div className={`rounded-md border border-blue-200 bg-blue-50/30 p-3 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-800">
          <ShieldCheck className="h-3.5 w-3.5" />
          Garanzie richieste dal cliente
          {selected.size > 0 && (
            <span className="ml-1 bg-blue-600 text-white text-[10px] font-bold rounded-full px-2 py-0.5">{selected.size}</span>
          )}
        </div>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />}
      </div>

      {error && <p className="text-xs text-red-600 py-2">{error}</p>}

      {!loading && items.length === 0 && !error && (
        <p className="text-xs text-slate-500 py-2 italic">
          Nessuna garanzia configurata{policyType ? ` per la tipologia "${policyType}"` : ""}. Aggiungile da Gest. sez. Polizze → Garanzie Polizze.
        </p>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1.5 max-h-80 overflow-y-auto pr-1">
          {[...items].sort((a, b) => {
            const pa = PRIORITY_ORDER[a.priority] || 9;
            const pb = PRIORITY_ORDER[b.priority] || 9;
            if (pa !== pb) return pa - pb;
            return (a.sort_order || 0) - (b.sort_order || 0);
          }).map((c) => {
            const checked = selected.has(c.code);
            const prio = c.priority || "utile";
            return (
              <label
                key={c.id || c.code}
                data-testid={`${testid}-${c.code}`}
                className={`flex items-start gap-2 text-sm cursor-pointer rounded px-2 py-1.5 transition-colors ${checked ? "bg-blue-100/70" : "hover:bg-white/70"}`}
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(c.code, c.label)} className="mt-0.5" />
                <span className="flex-1 leading-tight min-w-0">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-slate-800">{c.label}</span>
                    <span className={`text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border ${PRIORITY_STYLES[prio]}`}>
                      {PRIORITY_LABELS[prio]}
                    </span>
                  </span>
                  {c.description && <span className="block text-[10.5px] text-slate-500 mt-0.5">{c.description}</span>}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {selectedLabels.length > 0 && (
        <div className="mt-2.5 pt-2 border-t border-blue-200 text-[11px] text-slate-700">
          <span className="text-blue-700 font-semibold">Selezionate:</span>{" "}
          <span data-testid={`${testid}-summary`}>{selectedLabels.join(" · ")}</span>
        </div>
      )}
    </div>
  );
};

export default CoveragePicker;
